import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './Header';
import { BienestarAgendaSnippet } from './bienestar/BienestarAgenda';
import {
  BienestarCrossPaths,
  BienestarLandingHero,
  BienestarProfessionalBlock,
  BienestarStoreCta,
  useBienestarPageMeta,
} from './bienestar/BienestarShared';
import {
  BIENESTAR_LANDING_HERO,
  YOGA_PROGRAMS,
  goReservar,
  navigateTo,
  submitBienestarInterest,
} from './bienestar/bienestarNav';
import './BienestarPage.css';

const BienestarYogaPage = () => {
  const { t } = useTranslation();
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [interestEmail, setInterestEmail] = useState('');
  const [interestSubmitting, setInterestSubmitting] = useState(false);
  const [interestSuccess, setInterestSuccess] = useState(false);
  const [interestError, setInterestError] = useState(null);

  useBienestarPageMeta('bienestarLandings.metaYogaTitle', 'bienestarLandings.metaYogaDesc');

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bienestar-page bienestar-page--landing">
      <Header />
      <BienestarLandingHero
        kickerKey="bienestarLandings.yogaKicker"
        titleKey="bienestarLandings.yogaHeroTitle"
        leadKey="bienestarLandings.yogaHeroLead"
        ctaKey="bienestarHub.yogaCta"
        onCta={() => goReservar('yoga-1a-1')}
        imageSrc={BIENESTAR_LANDING_HERO.yoga.src}
        fallbackSrc={BIENESTAR_LANDING_HERO.yoga.fallbackSrc}
      />

      <main className="bienestar-main bw-main">
        <div className="container bw-container">
          <article className="bw-session-card bw-session-card--yoga bw-session-card--solo">
            <span className="bw-session-tag">Yoga</span>
            <h2>{t('bienestarHub.yogaCardTitle')}</h2>
            <p className="bw-session-desc">{t('bienestarHub.yogaCardDesc')}</p>
            <ul className="bw-session-meta">
              <li><i className="fas fa-clock" /> {t('bienestarHub.yogaCardMeta1')}</li>
              <li><i className="fas fa-calendar-day" /> {t('bienestarHub.yogaCardMeta2')}</li>
              <li><i className="fas fa-video" /> {t('bienestarHub.yogaCardMeta3')}</li>
            </ul>
            <div className="bw-prices">
              <span className="bw-price-main">{t('bienestarHub.yogaPriceSingle')} <small>COP</small></span>
              <span className="bw-price-alt">{t('bienestarHub.yogaPricePresencial')}</span>
              <span className="bw-price-pack">{t('bienestarHub.yogaPricePack4')}</span>
              <span className="bw-price-pack">{t('bienestarHub.yogaPricePack8')}</span>
            </div>
            <p className="bw-contact-note">{t('bienestarHub.contactSoon')}</p>
            <button type="button" className="bw-cta" onClick={() => goReservar('yoga-1a-1')}>
              {t('bienestarHub.yogaCta')}
            </button>
          </article>

          <section className="bw-section">
            <header className="bw-section-header">
              <h2 className="bw-section-title">{t('bienestarHub.programsTitle')}</h2>
              <p className="bw-section-sub">{t('bienestarHub.programsSub')}</p>
            </header>
            <div className="bw-program-grid">
              {YOGA_PROGRAMS.map((prog) => (
                <article
                  key={prog.id}
                  className="bw-program-card"
                  onClick={() => setSelectedProgram(prog)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedProgram(prog)}
                >
                  <span className="bw-program-badge">{t(`bienestarHub.${prog.badgeKey}`)}</span>
                  <h3>{t(`bienestarHub.${prog.titleKey}`)}</h3>
                  <p>{t(`bienestarHub.${prog.descKey}`)}</p>
                  <span className="bw-program-price">{t(`bienestarHub.${prog.priceKey}`)} COP</span>
                </article>
              ))}
            </div>
          </section>

          <BienestarProfessionalBlock variant="yoga" />
          <BienestarAgendaSnippet serviceSlug="yoga-1a-1" />
          <BienestarStoreCta variant="yoga" />
          <BienestarCrossPaths current="yoga" />
          <button type="button" className="bw-back-hub" onClick={() => navigateTo('/servicios#bienestar-experiencias')}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> {t('bienestarLandings.backHub')}
          </button>
        </div>
      </main>

      {selectedProgram && (
        <div className="event-modal-overlay" onClick={() => setSelectedProgram(null)}>
          <div className="event-modal javiland-course-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setSelectedProgram(null)} aria-label="Cerrar">
              <i className="fas fa-times" />
            </button>
            <div className="event-modal-content">
              <div className="event-modal-header">
                <span className="event-program-badge">{t(`bienestarHub.${selectedProgram.badgeKey}`)}</span>
                <h3 className="event-modal-title">{t(`bienestarHub.${selectedProgram.titleKey}`)}</h3>
                <p className="javiland-course-philosophy">{selectedProgram.philosophy}</p>
              </div>
              <div className="event-modal-body">
                <div className="event-description">
                  <h4>{t('bienestarLandings.programDescLabel')}</h4>
                  <p>{selectedProgram.fullDesc}</p>
                  <p className="bw-program-price-modal">{t(`bienestarHub.${selectedProgram.priceKey}`)} COP</p>
                </div>
                <div className="javiland-interest-section">
                  <h4>{t('bienestarHub.programInterestNote')}</h4>
                  {interestSuccess ? (
                    <div className="interest-success">
                      <i className="fas fa-check-circle" /> {t('bienestarLandings.interestSuccess')}
                    </div>
                  ) : (
                    <form
                      className="interest-form"
                      onSubmit={(e) => submitBienestarInterest(e, selectedProgram.id, interestEmail, {
                        setSubmitting: setInterestSubmitting,
                        setSuccess: setInterestSuccess,
                        setError: setInterestError,
                        setEmail: setInterestEmail,
                      })}
                    >
                      <div className="interest-form-row">
                        <input
                          type="email"
                          placeholder={t('bienestarLandings.emailPlaceholder')}
                          value={interestEmail}
                          onChange={(e) => setInterestEmail(e.target.value)}
                          required
                        />
                        <button type="submit" className="interest-btn" disabled={interestSubmitting}>
                          {interestSubmitting ? '...' : t('bienestarHub.communityCta')}
                        </button>
                      </div>
                      {interestError && (
                        <div className="interest-error"><i className="fas fa-exclamation-circle" /> {interestError}</div>
                      )}
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BienestarYogaPage;
